import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';

import { UserRegistrationResponse } from '../types';

export type SendOTP = {
  captcha_token: string;
  country?: string;
  email_check?: boolean;
  lsq_update: boolean;
  mobile_number_validation: boolean;
  otp_in_whatsapp: boolean;
  otp_source: string;
  otp_type: string;
  platform?: string;
  request_source: string;
};

export type SubmitOTP = {
  otp: string;
  otp_type: string;
  source: string;
  mobile_number?: string;
  email?: string;
};

export const sendOTP = (
  data: SendOTP
): Promise<UserRegistrationResponse.GetOtpResponse> => {
  const payload = { ...data, mobile_number_validation: true };
  return axios.post('/user/v1/send-otp', payload);
};

export const resendOTP = (
  data: SendOTP
): Promise<UserRegistrationResponse.GetOtpResponse> => {
  const payload = { ...data, mobile_number_validation: true };
  return axios.post('/user/v1/resend-otp', payload);
};

export const submitOTP = (
  data: SubmitOTP | boolean
): Promise<UserRegistrationResponse.Root> => {
  return axios.post('/user/v2/otp-login', data);
};

// Mock handlers
const mockOtpResponse: UserRegistrationResponse.GetOtpResponse = {
  otp_sent: true,
  lead_id: 'lead-123',
};

const mockUserRegistrationResponse: UserRegistrationResponse.Root = {
  status: 'Success',
  message: 'OTP login successful',
  user: {
    id: 'user-123',
    email_hash: 'test@example.com',
    base_lead_id: 'lead-123',
    consent_granted: true,
    practice_id: 'practice-123',
    selected_profile_id: 'profile-123',
    selected_variant_id: 'variant-123',
  },
};

export const sendOtpMockHandler = http.post(
  `${env.API_URL}/user/v1/send-otp`,
  async ({
    request,
  }): Promise<HttpResponse<UserRegistrationResponse.GetOtpResponse>> => {
    const body = (await request.json()) as SendOTP;
    const { otp_source, otp_type, request_source, platform } = body;

    if (!otp_source || !otp_type || !request_source || !platform) {
      return HttpResponse.json(
        {
          otp_sent: false,
          lead_id: '',
        },
        { status: 400 }
      );
    }

    if (otp_source === 'rate-limited@example.com') {
      return HttpResponse.json(
        {
          otp_sent: false,
          lead_id: '',
        },
        { status: 429 }
      );
    }

    return HttpResponse.json(mockOtpResponse);
  }
);

export const resendOtpMockHandler = http.post(
  `${env.API_URL}/user/v1/resend-otp`,
  async ({
    request,
  }): Promise<HttpResponse<UserRegistrationResponse.GetOtpResponse>> => {
    const body = (await request.json()) as SendOTP;
    const { otp_source, otp_type, request_source, platform } = body;

    if (!otp_source || !otp_type || !request_source || !platform) {
      return HttpResponse.json(
        {
          otp_sent: false,
          lead_id: '',
        },
        { status: 400 }
      );
    }

    return HttpResponse.json({
      ...mockOtpResponse,
    });
  }
);

export const submitOtpMockHandler = http.post(
  `${env.API_URL}/user/v2/otp-login`,
  async ({ request }): Promise<HttpResponse<UserRegistrationResponse.Root>> => {
    const body = (await request.json()) as SubmitOTP | boolean;

    if (typeof body === 'boolean') {
      return HttpResponse.json(mockUserRegistrationResponse);
    }

    const { mobile_number, otp } = body;

    if (!mobile_number || !otp) {
      return HttpResponse.json(
        { status: 'Failure', message: 'Mobile number and OTP are required' },
        { status: 400 }
      );
    }

    if (otp !== '123456') {
      return HttpResponse.json(
        { status: 'Failure', message: 'Invalid OTP' },
        { status: 401 }
      );
    }

    return HttpResponse.json(mockUserRegistrationResponse);
  }
);
